import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import CustomTable from '../common/CustomTable';
import Button from '../common/Button';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { Party } from '../../shared/interfaces';
import type {
  ImportAccountPreMappedEntry,
  ImportAccountRow,
  ImportPreviewEntry,
} from '../../shared/importAccountTypes';
import { isImportRowPostable, isImportRowReadyToCommit } from '../../shared/importAccountTypes';
import {
  IMPORT_ACCOUNT_BATCH_EXCEEDED_MESSAGE,
  IMPORT_ACCOUNT_MAX_BATCH_ROWS,
} from '../../shared/importAccountConstants';
import { parseImportAccountFile } from '../../utils/importAccount/parseImportAccountFile';
import {
  applyFuzzyPartySuggestions,
  applyPreMappedParties,
  confirmSuggestedRow,
} from '../../utils/importAccount/mapUsernameToParty';
import {
  buildBulkMappingItems,
  buildImportRequestRows,
  bulkUpsertImportAccountUsernameMappings,
  executeImportAccount,
  listImportAccountUsernameMappings,
  parseImportAccountRows,
  previewImportAccount,
  uploadImportAccountFile,
  type ImportAccountRequestPayload,
} from '../../utils/importAccount/importAccountApi';
import {
  extractPartyCodeFromInput,
  lookupPartyByCode,
} from '../../utils/importAccount/lookupPartyByCode';
import ImportAccountRowPartyInput from './ImportAccountRowPartyInput';
import { getTodayDateIST } from '../../utils/util';

const PAGE_CARD = 'bg-white rounded-lg shadow-md border border-slate-200';
const LABEL_CLASS = 'block text-xs font-medium text-gray-700 mb-0.5';
const LABEL_TEXT_CLASS = 'text-xs font-medium text-gray-700';
const LABEL_ROW_CLASS = 'min-h-[18px] flex items-end mb-0.5';
const FIELD_ROW_CLASS = 'h-[34px] flex items-center gap-2';
const INPUT_BASE =
  'h-[34px] px-2 py-0 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 box-border';
const PRIMARY_BTN =
  'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
const SUCCESS_BTN =
  'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

const PREVIEW_BLOCKING_ERROR_CODES = new Set([
  'PARTY_UNMAPPED',
  'PARTY_NOT_FOUND',
  'SETTLEMENT_PARTY_NOT_FOUND',
  'INVALID_PERCENTAGE',
  'DUPLICATE_ROW_INDEX',
]);

type PartyFocusTarget = 'settlement' | string;

function createClientBatchId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function inputClass(error?: boolean, valid?: boolean): string {
  if (error) return `${INPUT_BASE} border-red-500`;
  if (valid) return `${INPUT_BASE} border-green-600`;
  return `${INPUT_BASE} border-gray-300`;
}

function formatAmount(value: number): string {
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function statusLabel(status: ImportAccountRow['status']): string {
  switch (status) {
    case 'pre_mapped':
      return 'Pre Mapped';
    case 'mapped':
      return 'Mapped';
    case 'suggested':
      return 'Suggested';
    default:
      return 'Pending';
  }
}

function statusClass(status: ImportAccountRow['status']): string {
  switch (status) {
    case 'pre_mapped':
      return 'text-green-700';
    case 'mapped':
      return 'text-green-800 font-semibold';
    case 'suggested':
      return 'text-amber-700';
    default:
      return 'text-red-700';
  }
}

function rowBackgroundClass(status: ImportAccountRow['status']): string {
  switch (status) {
    case 'pre_mapped':
      return 'bg-green-50';
    case 'mapped':
      return 'bg-green-100';
    case 'suggested':
      return 'bg-amber-50';
    default:
      return 'bg-red-50';
  }
}

const ImportAccount: React.FC = () => {
  const [parties, setParties] = useState<Party[]>([]);
  const [partyCode, setPartyCode] = useState('');
  const [validatedPartyCode, setValidatedPartyCode] = useState('');
  const [partyValidation, setPartyValidation] = useState<{
    isValid: boolean;
    error: string;
    partyName: string;
    partyId: number | null;
    currentBalance?: number;
  }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ImportAccountRow[]>([]);
  const [preMappedEntries, setPreMappedEntries] = useState<ImportAccountPreMappedEntry[]>([]);
  const [parseError, setParseError] = useState('');
  const [parsing, setParsing] = useState(false);
  const [recordDate, setRecordDate] = useState(getTodayDateIST());
  const [remark, setRemark] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewEntries, setPreviewEntries] = useState<ImportPreviewEntry[]>([]);
  const [previewTotals, setPreviewTotals] = useState({
    importAmount: 0,
    debit: 0,
    credit: 0,
    partnerDebit: 0,
    partnerCredit: 0,
  });
  const [previewHasBlockingErrors, setPreviewHasBlockingErrors] = useState(false);
  const [globalPercentage, setGlobalPercentage] = useState('0');
  const [importAccountFileId, setImportAccountFileId] = useState<number | null>(null);
  const [clientBatchId, setClientBatchId] = useState<string>(createClientBatchId());
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [importCompleted, setImportCompleted] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const partyInputRef = useRef<HTMLInputElement>(null);
  const focusedPartyTargetRef = useRef<PartyFocusTarget>('settlement');

  useEffect(() => {
    const loadParties = async () => {
      try {
        const response = await apiService.post<{ data?: Party[] } | Party[]>(
          apiRoutes.parties.lite,
          { page: 1, pageSize: 10000 },
        );
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setParties(list);
      } catch {
        setParties([]);
      }
    };
    loadParties();
  }, []);

  useEffect(() => {
    if (parties.length === 0) return;
    setRows((prev) => {
      if (prev.length === 0) return prev;
      const withPreMapped = applyPreMappedParties(prev, preMappedEntries, parties);
      return applyFuzzyPartySuggestions(withPreMapped, parties);
    });
  }, [parties, preMappedEntries]);

  const lookupParty = useCallback(
    (code: string) =>
      lookupPartyByCode(code, parties, (c) => apiService.get<Party>(apiRoutes.parties.getByCode(c))),
    [parties],
  );

  const validateAndFetchParty = useCallback(
    async (code: string) => {
      const result = await lookupParty(code);
      if (result.isValid && result.partyId) {
        setPartyValidation({
          isValid: true,
          error: '',
          partyName: result.partyName,
          partyId: result.partyId,
          currentBalance: result.currentBalance,
        });
        setValidatedPartyCode(result.partyCode.toUpperCase());
        if (partyInputRef.current) {
          partyInputRef.current.value = `${result.partyCode} - ${result.partyName}`;
        }
        setPartyCode(result.partyCode.toUpperCase());
      } else {
        setPartyValidation({
          isValid: false,
          error: result.error,
          partyName: '',
          partyId: null,
        });
        setValidatedPartyCode('');
      }
      return result;
    },
    [lookupParty],
  );

  const buildImportPayload = useCallback((): ImportAccountRequestPayload | null => {
    if (!importAccountFileId || !partyValidation.partyId || !validatedPartyCode) return null;

    try {
      const requestRows = buildImportRequestRows(rows);
      return {
        importAccountFileId,
        settlementPartyId: partyValidation.partyId,
        recordDate,
        remark: remark.trim(),
        clientBatchId,
        partnerPartyCode: validatedPartyCode,
        rows: requestRows,
      };
    } catch {
      return null;
    }
  }, [
    importAccountFileId,
    partyValidation.partyId,
    validatedPartyCode,
    recordDate,
    remark,
    clientBatchId,
    rows,
  ]);

  const applyRowPartyLookup = useCallback(
    (rowId: string, result: Awaited<ReturnType<typeof lookupParty>>) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.id !== rowId) return row;
          if (result.isValid && result.partyId) {
            return {
              ...row,
              matchedPartyId: result.partyId,
              matchedPartyCode: result.partyCode,
              matchedPartyName: result.partyName,
              partyCodeInput: `${result.partyCode} - ${result.partyName}`,
              validatedRowPartyCode: result.partyCode.toUpperCase(),
              partyInputError: '',
              status: 'mapped' as const,
            };
          }
          return {
            ...row,
            matchedPartyId: undefined,
            matchedPartyCode: undefined,
            matchedPartyName: undefined,
            validatedRowPartyCode: '',
            partyInputError: result.error || 'Invalid party code',
            status: 'pending' as const,
          };
        }),
      );
      setShowPreview(false);
    },
    [],
  );

  const handleRowPartyCodeChange = (rowId: string, value: string) => {
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const isNewValue = codeOnly !== row.validatedRowPartyCode;
        if (!isNewValue) {
          return { ...row, partyCodeInput: value };
        }
        return {
          ...row,
          partyCodeInput: codeMatch ? codeOnly : value.toUpperCase(),
          validatedRowPartyCode: '',
          matchedPartyId: undefined,
          matchedPartyCode: undefined,
          matchedPartyName: undefined,
          partyInputError: '',
          status: 'pending' as const,
        };
      }),
    );
    setShowPreview(false);
  };

  const handleRowPartyCodeBlur = async (rowId: string, value: string) => {
    const codeToValidate = extractPartyCodeFromInput(value);
    if (!codeToValidate) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === rowId
            ? {
                ...row,
                partyCodeInput: '',
                validatedRowPartyCode: '',
                matchedPartyId: undefined,
                matchedPartyCode: undefined,
                matchedPartyName: undefined,
                partyInputError: '',
                status: 'pending' as const,
              }
            : row,
        ),
      );
      return;
    }
    const result = await lookupParty(codeToValidate);
    applyRowPartyLookup(rowId, result);
  };

  const handleRowPartyCodeKeyDown = async (
    rowId: string,
    value: string,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'F3') {
      e.preventDefault();
      focusedPartyTargetRef.current = rowId;
      window.dispatchEvent(new CustomEvent('open-party-list-reference'));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const codeToValidate = extractPartyCodeFromInput(value);
      if (!codeToValidate) return;
      const row = rows.find((r) => r.id === rowId);
      if (row && codeToValidate === row.validatedRowPartyCode) return;
      const result = await lookupParty(codeToValidate);
      applyRowPartyLookup(rowId, result);
    }
  };

  const handleRowPartyFocus = (rowId: string) => {
    focusedPartyTargetRef.current = rowId;
  };

  const handlePartyCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    const isNewValue = codeOnly !== validatedPartyCode;

    if (isNewValue) {
      setPartyCode(codeOnly);
      setValidatedPartyCode('');
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null,
      });
      if (partyInputRef.current && value !== codeOnly) {
        partyInputRef.current.value = codeOnly;
      }
    } else {
      setPartyCode(codeOnly);
    }
  };

  const handlePartyCodeBlur = async () => {
    const value = partyInputRef.current?.value || partyCode;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();

    if (codeToValidate) {
      if (codeToValidate !== validatedPartyCode) {
        await validateAndFetchParty(codeToValidate);
      }
    } else {
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null,
      });
      setValidatedPartyCode('');
      if (partyInputRef.current) {
        partyInputRef.current.value = '';
      }
    }
  };

  const handlePartyCodeKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'F3') {
      e.preventDefault();
      focusedPartyTargetRef.current = 'settlement';
      window.dispatchEvent(new CustomEvent('open-party-list-reference'));
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const value = partyInputRef.current?.value || partyCode;
      const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
      const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
      if (codeToValidate && codeToValidate !== validatedPartyCode) {
        await validateAndFetchParty(codeToValidate);
      }
    }
  };

  useEffect(() => {
    const onPartySelected = (e: Event) => {
      const detail = (e as CustomEvent<{ partyCode?: string }>).detail;
      if (!detail?.partyCode) return;
      const target = focusedPartyTargetRef.current;
      if (target === 'settlement') {
        void validateAndFetchParty(detail.partyCode);
        return;
      }
      void lookupParty(detail.partyCode).then((result) => applyRowPartyLookup(target, result));
    };
    window.addEventListener('party-selected-from-reference', onPartySelected as EventListener);
    return () =>
      window.removeEventListener('party-selected-from-reference', onPartySelected as EventListener);
  }, [validateAndFetchParty, lookupParty, applyRowPartyLookup]);

  const handleConfirmSuggested = (rowId: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? confirmSuggestedRow(row) : row)),
    );
    setShowPreview(false);
  };

  const handleFileUpload = useCallback(
    async (file: File | null) => {
      if (!file) {
        setSelectedFile(null);
        setRows([]);
        setPreMappedEntries([]);
        setImportAccountFileId(null);
        setParseError('');
        setShowPreview(false);
        setPreviewEntries([]);
        setPreviewHasBlockingErrors(false);
        setPreviewTotals({ importAmount: 0, debit: 0, credit: 0, partnerDebit: 0, partnerCredit: 0 });
        setImportCompleted(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setSelectedFile(file);
      setParseError('');
      setRows([]);
      setPreMappedEntries([]);
      setImportAccountFileId(null);
      setImportCompleted(false);
      setShowPreview(false);
      setPreviewEntries([]);
      setGlobalPercentage('0');
      setParsing(true);

      try {
        const parsed = await parseImportAccountFile(file);
        if (parsed.length === 0) {
          setParseError('No data rows found in the file.');
          return;
        }
        if (parsed.length > IMPORT_ACCOUNT_MAX_BATCH_ROWS) {
          setParseError(IMPORT_ACCOUNT_BATCH_EXCEEDED_MESSAGE);
          return;
        }

        const uploaded = await uploadImportAccountFile(file);
        setImportAccountFileId(uploaded.fileId);

        const [parsedApi, preMapped] = await Promise.all([
          parseImportAccountRows(uploaded.fileId, parsed),
          listImportAccountUsernameMappings(),
        ]);

        const serverResolvedRows = parsed.map((row) => {
          const resolved = parsedApi.rows.find((item) => item.rowIndex === row.rowIndex);
          if (!resolved?.matchedPartyId || !resolved.matchedPartyCode) return row;
          return {
            ...row,
            matchedPartyId: resolved.matchedPartyId,
            matchedPartyCode: resolved.matchedPartyCode ?? undefined,
            matchedPartyName: resolved.matchedPartyName ?? undefined,
            partyCodeInput: resolved.matchedPartyName
              ? `${resolved.matchedPartyCode} - ${resolved.matchedPartyName}`
              : String(resolved.matchedPartyCode),
            validatedRowPartyCode: String(resolved.matchedPartyCode).toUpperCase(),
            status: resolved.mappingSource === 'Table' ? ('pre_mapped' as const) : ('suggested' as const),
            partyInputError: resolved.error ?? '',
          };
        });

        setPreMappedEntries(preMapped);
        const withPreMapped = applyPreMappedParties(serverResolvedRows, preMapped, parties);
        setRows(applyFuzzyPartySuggestions(withPreMapped, parties).map((row) => ({
          ...row,
          percentage: row.percentage.trim() === '' ? '0' : row.percentage,
        })));
        setClientBatchId(createClientBatchId());
      } catch (error) {
        setParseError(error instanceof Error ? error.message : 'Failed to parse file.');
      } finally {
        setParsing(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [parties],
  );

  const handleGlobalPercentageChange = (value: string) => {
    setGlobalPercentage(value);
    setRows((prev) => prev.map((row) => ({ ...row, percentage: value })));
    setShowPreview(false);
  };

  const updateRowPercentage = (rowId: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, percentage: value } : row)),
    );
    setShowPreview(false);
  };

  const summary = useMemo(() => {
    const preMapped = rows.filter((r) => r.status === 'pre_mapped').length;
    const mapped = rows.filter((r) => r.status === 'mapped').length;
    const suggested = rows.filter((r) => r.status === 'suggested').length;
    const pending = rows.filter((r) => r.status === 'pending').length;
    const totalClientPl = rows.reduce((sum, r) => sum + r.clientPl, 0);
    const readyToCommit = rows.every((r) => isImportRowReadyToCommit(r.status));
    return {
      total: rows.length,
      preMapped,
      mapped,
      suggested,
      pending,
      totalClientPl,
      readyToCommit,
    };
  }, [rows]);

  const handlePreview = async () => {
    if (!partyValidation.isValid || !partyValidation.partyId) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: 'Select a valid party code first.', type: 'error' },
        }),
      );
      return;
    }

    if (!summary.readyToCommit) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message:
              'All rows must be Pre Mapped or Mapped before preview. Confirm suggested mappings or enter party codes for pending rows.',
            type: 'error',
          },
        }),
      );
      return;
    }

    const postableCount = rows.filter(isImportRowPostable).length;
    if (postableCount === 0) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message:
              'No rows to import. Enter partner % and ensure at least one row has non-zero Client (P/L).',
            type: 'error',
          },
        }),
      );
      return;
    }

    const invalidRows = rows.filter((r) => {
      if (!isImportRowReadyToCommit(r.status) || r.clientPl === 0) return false;
      const pct = r.percentage.trim();
      if (pct === '') return true;
      const value = Number(pct);
      return !Number.isFinite(value) || value < 0 || value > 100;
    });
    if (invalidRows.length > 0) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: 'Enter a valid partner % (0–100) for all mapped rows with non-zero Client (P/L).',
            type: 'error',
          },
        }),
      );
      return;
    }

    if (!importAccountFileId) {
      setParseError('Please upload and parse a file first.');
      return;
    }

    const payload = buildImportPayload();
    if (!payload) return;

    try {
      setPreviewLoading(true);
      await bulkUpsertImportAccountUsernameMappings(buildBulkMappingItems(rows));
      const response = await previewImportAccount(payload);

      const blockingErrors = (response.errors ?? []).filter((err) =>
        PREVIEW_BLOCKING_ERROR_CODES.has(err.code),
      );
      if (blockingErrors.length > 0) {
        setPreviewHasBlockingErrors(true);
        window.dispatchEvent(
          new CustomEvent('show-toast', {
            detail: {
              message: blockingErrors.map((e) => e.message).join(' '),
              type: 'error',
            },
          }),
        );
      } else {
        setPreviewHasBlockingErrors(false);
      }

      const mappedEntries: ImportPreviewEntry[] = response.entries.map((entry) => ({
        rowId: `row-${entry.rowIndex}`,
        userNameRaw:
          rows.find((r) => r.rowIndex === entry.rowIndex)?.userNameRaw ??
          `${entry.loginId ?? ''}${entry.displayName ? ` (${entry.displayName})` : ''}`,
        matchedPartyCode: entry.matchedPartyCode,
        matchedPartyName: entry.matchedPartyName,
        percentage: entry.percentage,
        importAmount: entry.importAmount,
        debit: entry.rowPartyDebit,
        credit: entry.rowPartyCredit,
        partnerDebit: entry.partnerDebit ?? 0,
        partnerCredit: entry.partnerCredit ?? 0,
        settlementIsDebit: (entry.settlementDebit ?? 0) > 0,
      }));

      const totalImportAmount = mappedEntries.reduce((sum, item) => sum + item.importAmount, 0);
      const totalDebit = mappedEntries.reduce((sum, item) => sum + item.debit, 0);
      const totalCredit = mappedEntries.reduce((sum, item) => sum + item.credit, 0);
      const totalPartnerDebit = mappedEntries.reduce(
        (sum, item) => sum + (item.partnerDebit ?? 0),
        0,
      );
      const totalPartnerCredit = mappedEntries.reduce(
        (sum, item) => sum + (item.partnerCredit ?? 0),
        0,
      );

      setPreviewEntries(mappedEntries);
      setPreviewTotals({
        importAmount: totalImportAmount,
        debit: totalDebit,
        credit: totalCredit,
        partnerDebit: totalPartnerDebit,
        partnerCredit: totalPartnerCredit,
      });
      setShowPreview(true);
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: error instanceof Error ? error.message : 'Failed to generate preview.',
            type: 'error',
          },
        }),
      );
      setShowPreview(false);
      setPreviewEntries([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importAccountFileId || !partyValidation.partyId || importCompleted) return;

    const payload = buildImportPayload();
    if (!payload) return;

    try {
      setConfirmLoading(true);
      await bulkUpsertImportAccountUsernameMappings(buildBulkMappingItems(rows));
      const response = await executeImportAccount(payload);
      setImportCompleted(true);
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: `Import posted successfully (Batch #${response.importBatchId}).`,
            type: 'success',
          },
        }),
      );
      setClientBatchId(createClientBatchId());
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: error instanceof Error ? error.message : 'Failed to post import.',
            type: 'error',
          },
        }),
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  const canPreview =
    partyValidation.isValid &&
    rows.length > 0 &&
    !parsing &&
    !previewLoading &&
    summary.readyToCommit &&
    !!importAccountFileId;

  const canConfirmImport =
    showPreview &&
    previewEntries.length > 0 &&
    summary.readyToCommit &&
    !confirmLoading &&
    !importCompleted &&
    !previewHasBlockingErrors;

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen space-y-4">
      <div className={PAGE_CARD}>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-1.5 rounded-lg shadow-sm">
            <ArrowUpTrayIcon className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Import Account</h2>
        </div>
      </div>

      <div className={`${PAGE_CARD} p-4 space-y-2`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div className="flex flex-col min-w-0">
            <label className={LABEL_ROW_CLASS}>
              <span className={LABEL_TEXT_CLASS}>
                Party code <span className="text-red-500">*</span>{' '}
                <span className="text-gray-500 font-normal">(settlement / partner, F3)</span>
              </span>
            </label>
            <div className={FIELD_ROW_CLASS}>
              <input
                ref={partyInputRef}
                id="import-account-party-code"
                type="text"
                defaultValue=""
                onChange={handlePartyCodeChange}
                onBlur={handlePartyCodeBlur}
                onKeyDown={handlePartyCodeKeyDown}
                onFocus={() => {
                  focusedPartyTargetRef.current = 'settlement';
                }}
                maxLength={50}
                placeholder="Party Code"
                className={`${inputClass(!!partyValidation.error, partyValidation.isValid)} flex-1 min-w-0`}
              />
              {partyValidation.isValid && partyValidation.currentBalance !== undefined && (
                <span className="text-xs text-slate-700 whitespace-nowrap shrink-0">
                  Balance:{' '}
                  <span className="font-semibold text-slate-800">
                    {formatAmount(partyValidation.currentBalance)}
                  </span>
                </span>
              )}
            </div>
            <div className="min-h-[16px] mt-0.5">
              {partyValidation.error && (
                <div className="text-red-500 text-xs">{partyValidation.error}</div>
              )}
            </div>
          </div>

          <div className="flex flex-col min-w-0">
            <label className={LABEL_ROW_CLASS}>
              <span className={LABEL_TEXT_CLASS}>Import file</span>
            </label>
            <div
              className={`${FIELD_ROW_CLASS} w-full border-2 border-dashed border-gray-300 rounded-md px-2 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors ${
                parsing ? 'opacity-70 pointer-events-none' : ''
              }`}
              onClick={() => !parsing && fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (parsing) return;
                const file = e.dataTransfer.files?.[0];
                if (file) void handleFileUpload(file);
              }}
            >
              <ArrowUpTrayIcon className="h-4 w-4 text-blue-600 shrink-0" />
              <div className="min-w-0 flex-1 text-left">
                {selectedFile ? (
                  <p className="text-sm text-slate-800 truncate leading-tight">
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    {parsing ? ' · Parsing…' : ''}
                  </p>
                ) : (
                  <p className="text-sm text-slate-600 truncate leading-tight">
                    Drop .xlsx, .xls, or .csv — click to browse
                  </p>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.txt"
                className="hidden"
                onChange={(e) => void handleFileUpload(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="min-h-[16px] mt-0.5" />
          </div>
        </div>

        {parseError && <p className="text-sm text-red-600 font-medium">{parseError}</p>}
        <p className="text-xs text-slate-500">
          Maximum {IMPORT_ACCOUNT_MAX_BATCH_ROWS} rows per batch. Split larger files into chunks of{' '}
          {IMPORT_ACCOUNT_MAX_BATCH_ROWS}.
        </p>
      </div>

      {rows.length > 0 && (
        <div className={`${PAGE_CARD} overflow-hidden`}>
          <div className="md:hidden px-4 py-2 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 space-y-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600">
              <span>
                Rows: <span className="font-semibold text-slate-800">{summary.total}</span>
              </span>
              <span>
                Pre Mapped:{' '}
                <span className="font-semibold text-green-700">{summary.preMapped}</span>
              </span>
              <span>
                Mapped: <span className="font-semibold text-green-800">{summary.mapped}</span>
              </span>
              <span>
                Suggested:{' '}
                <span className="font-semibold text-amber-700">{summary.suggested}</span>
              </span>
              <span>
                Pending: <span className="font-semibold text-red-700">{summary.pending}</span>
              </span>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                min={0}
                max={100}
                value={globalPercentage}
                onChange={(e) => handleGlobalPercentageChange(e.target.value)}
                className={`${INPUT_BASE} border-gray-300 w-20 text-right shrink-0`}
                placeholder="%"
              />
              <label className="ml-2 text-xs font-medium text-gray-700">Partner %</label>
            </div>
          </div>
          <CustomTable<ImportAccountRow>
            variant="gameList"
            data={rows}
            showHeader={false}
            showRowsPerPage={false}
            showPagination={false}
            leadingHeaderRow={
              <tr className="border-b border-slate-200">
                <th
                  colSpan={3}
                  className="px-4 py-2 text-left font-normal whitespace-nowrap"
                >
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-slate-600">
                    <span>
                      Rows: <span className="font-semibold text-slate-800">{summary.total}</span>
                    </span>
                    <span>
                      Pre Mapped:{' '}
                      <span className="font-semibold text-green-700">{summary.preMapped}</span>
                    </span>
                    <span>
                      Mapped:{' '}
                      <span className="font-semibold text-green-800">{summary.mapped}</span>
                    </span>
                    <span>
                      Suggested:{' '}
                      <span className="font-semibold text-amber-700">{summary.suggested}</span>
                    </span>
                    <span>
                      Pending:{' '}
                      <span className="font-semibold text-red-700">{summary.pending}</span>
                    </span>
                    <span>
                      Total Client (P/L):{' '}
                      <span className="font-semibold text-slate-800">
                        {formatAmount(summary.totalClientPl)}
                      </span>
                    </span>
                  </div>
                </th>
                <th className="px-4 py-2 text-left font-normal align-middle">
                  <div className="flex items-center">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={globalPercentage}
                      onChange={(e) => handleGlobalPercentageChange(e.target.value)}
                      className={`${INPUT_BASE} border-gray-300 w-20 text-right shrink-0`}
                      placeholder="%"
                    />
                    <label className="ml-2 text-xs font-medium text-gray-700 whitespace-nowrap">
                      Partner %
                    </label>
                  </div>
                </th>
                <th colSpan={2} className="px-4 py-2" />
              </tr>
            }
            columns={[
              { key: 'userNameRaw', label: 'User Name' },
              {
                key: 'clientPl',
                label: 'Client (P/L)',
                align: 'right',
                render: (item) => formatAmount(item.clientPl),
              },
              { key: 'accountType', label: 'Account type' },
              {
                key: 'percentage',
                label: 'Partner % *',
                render: (item) => (
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={item.percentage}
                    onChange={(e) => updateRowPercentage(item.id, e.target.value)}
                    className={`${INPUT_BASE} border-gray-300 w-20 text-right`}
                    placeholder="%"
                  />
                ),
              },
              {
                key: 'matchedPartyCode',
                label: 'Party code',
                render: (item) => (
                  <ImportAccountRowPartyInput
                    row={item}
                    onChange={handleRowPartyCodeChange}
                    onBlur={handleRowPartyCodeBlur}
                    onFocus={handleRowPartyFocus}
                    onKeyDown={handleRowPartyCodeKeyDown}
                    onConfirmSuggested={handleConfirmSuggested}
                  />
                ),
              },
              {
                key: 'status',
                label: 'Status',
                render: (item) => (
                  <span className={statusClass(item.status)}>{statusLabel(item.status)}</span>
                ),
              },
            ]}
            rowsPerPage={rows.length || 1}
            onRowsPerPageChange={() => {}}
            totalPages={1}
            currentPage={0}
            onPageChange={() => {}}
            loading={false}
            getId={(item) => Number(item.id.replace(/\D/g, '')) || 0}
            getRowClassName={(item) => rowBackgroundClass(item.status)}
            />
        </div>
      )}

      {showPreview && previewEntries.length > 0 && (
        <div className={`${PAGE_CARD} overflow-hidden`}>
          <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">
              Calculated entries (party: {validatedPartyCode || '—'})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full w-full table-fixed divide-y divide-slate-200 text-sm">
              <colgroup>
                <col className="w-[12%]" />
                <col className="w-[22%]" />
                <col className="w-[9%]" />
                <col className="w-[12%]" />
                <col className="w-[11%]" />
                <col className="w-[11%]" />
                <col className="w-[11%]" />
                <col className="w-[11%]" />
              </colgroup>
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Party
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    From Excel user
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Partner %
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Import amount
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Debit
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Credit
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Partner Dr
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Partner Cr
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {previewEntries.map((entry) => (
                  <tr key={entry.rowId} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-800">{entry.matchedPartyCode}</td>
                    <td className="px-4 py-2 text-slate-800">{entry.userNameRaw}</td>
                    <td className="px-4 py-2 text-right text-slate-800 tabular-nums">{entry.percentage}</td>
                    <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                      {formatAmount(entry.importAmount)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                      {entry.debit > 0 ? formatAmount(entry.debit) : ''}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                      {entry.credit > 0 ? formatAmount(entry.credit) : ''}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                      {(entry.partnerDebit ?? 0) > 0 ? formatAmount(entry.partnerDebit ?? 0) : ''}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                      {(entry.partnerCredit ?? 0) > 0 ? formatAmount(entry.partnerCredit ?? 0) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 font-medium border-t border-slate-200">
                <tr>
                  <td colSpan={2} className="px-4 py-2 text-left text-slate-700">
                    Totals
                  </td>
                  <td className="px-4 py-2" />
                  <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                    {formatAmount(previewTotals.importAmount)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                    {formatAmount(previewTotals.debit)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                    {formatAmount(previewTotals.credit)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                    {previewTotals.partnerDebit > 0 ? formatAmount(previewTotals.partnerDebit) : ''}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800 tabular-nums">
                    {previewTotals.partnerCredit > 0
                      ? formatAmount(previewTotals.partnerCredit)
                      : ''}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className={`${PAGE_CARD} p-4 space-y-4`}>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col min-w-[200px]">
              <label className={LABEL_CLASS}>
                Record date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={recordDate}
                onChange={(e) => setRecordDate(e.target.value)}
                className={`${INPUT_BASE} border-gray-300`}
              />
            </div>
            <div className="flex flex-col flex-1 min-w-[240px]">
              <label className={LABEL_CLASS}>Remark (optional)</label>
              <input
                type="text"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                className={`${INPUT_BASE} border-gray-300`}
                placeholder="Import from exchange export"
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button onClick={handlePreview} disabled={!canPreview} className={PRIMARY_BTN}>
              {previewLoading ? 'Previewing...' : 'Preview import'}
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={!canConfirmImport}
              className={SUCCESS_BTN}
            >
              {confirmLoading ? 'Posting...' : importCompleted ? 'Import posted' : 'Confirm import'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportAccount;
